/**
 * Navigation desktop — ouverture/fermeture des méga-menus.
 *
 * Souris : ouverture au survol avec DÉLAI D'INTENTION de 300 ms (Baymard :
 * 300-500 ms anti-scintillement — un pointeur qui traverse la barre n'ouvre
 * rien) ; instantanée si un autre panneau est déjà ouvert (l'utilisateur
 * explore la barre) ; fermeture différée de 300 ms (tolère les trajets en
 * diagonale). Clavier/clic : le bouton chevron ouvre et ferme SANS délai
 * (aria-expanded), Échap ferme et rend le focus au bouton, la sortie du focus
 * ou un clic à l'extérieur ferment aussi.
 *
 * Sans JavaScript : les panneaux s'ouvrent au survol et au focus via la règle
 * CSS sous @media (scripting: none) — voir section-header.css.
 */
const HOVER_OPEN_DELAY = 300;
const HOVER_CLOSE_DELAY = 300;

class HeaderMenu extends HTMLElement {
  #closeTimer = null;
  #openTimer = null;

  connectedCallback() {
    this.#items().forEach((item) => {
      const toggle = item.querySelector('.header__menu-toggle');
      if (!toggle) return;

      item.addEventListener('mouseenter', () => {
        clearTimeout(this.#closeTimer);
        clearTimeout(this.#openTimer);
        const anotherOpen = this.querySelector('.header__menu-item--has-mega[data-open]');
        const delay = anotherOpen ? 0 : HOVER_OPEN_DELAY;
        this.#openTimer = setTimeout(() => this.#open(item), delay);
      });
      item.addEventListener('mouseleave', () => {
        clearTimeout(this.#openTimer);
        this.#closeTimer = setTimeout(() => this.#close(item), HOVER_CLOSE_DELAY);
      });

      toggle.addEventListener('click', () => {
        clearTimeout(this.#openTimer);
        clearTimeout(this.#closeTimer);
        if (item.hasAttribute('data-open')) {
          this.#close(item);
        } else {
          this.#open(item);
        }
      });

      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !item.hasAttribute('data-open')) return;
        event.stopPropagation();
        this.#close(item);
        toggle.focus();
      });

      item.addEventListener('focusout', (event) => {
        if (item.contains(event.relatedTarget)) return;
        this.#close(item);
      });
    });

    document.addEventListener('click', (event) => {
      if (this.contains(event.target)) return;
      this.#closeAll();
    });
  }

  #items() {
    return Array.from(this.querySelectorAll('.header__menu-item--has-mega'));
  }

  #open(item) {
    this.#items().forEach((other) => {
      if (other !== item) this.#close(other);
    });
    item.setAttribute('data-open', '');
    item.querySelector('.header__menu-toggle')?.setAttribute('aria-expanded', 'true');
  }

  #close(item) {
    item.removeAttribute('data-open');
    item.querySelector('.header__menu-toggle')?.setAttribute('aria-expanded', 'false');
  }

  #closeAll() {
    this.#items().forEach((item) => this.#close(item));
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
